import { useCallback, useEffect, useRef, useState } from 'react';
import { NyceBuddyChat, type ChatUiEvent, type NyceBuddyChatHandle } from '@nycelogic/nycebuddy-chat-ui';
import { client, logout, prefs, session, type LoginInfo } from '../auth/session';
import { useSessionKeepAlive } from '../auth/useSession';

export function Chat({ login, onLoggedOut }: { login: LoginInfo; onLoggedOut: () => void }) {
  const ref = useRef<NyceBuddyChatHandle>(null);
  useSessionKeepAlive(true);
  const [theme, setTheme] = useState(prefs.theme());
  const onThemeChange = useCallback((t: 'dark' | 'light' | 'auto') => {
    prefs.setTheme(t);
    setTheme(t);
    document.body.dataset.theme = t === 'auto' ? '' : t;
  }, []);
  // Deep link from the admin audit page (and shared links): `/?conversation=<id>` opens that conversation, then the
  // parameter is dropped from the address so a reload keeps the normal session behaviour.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('conversation');
    if (!id) return;
    params.delete('conversation');
    const rest = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`
    );
    let alive = true;
    client.conversations
      .get(id)
      .then((c) => {
        if (alive && c?.sessionId) ref.current?.loadConversation(c.sessionId);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const onEvent = useCallback(
    (e: ChatUiEvent) => {
      if (e.type === 'auth-expired') onLoggedOut();
    },
    [onLoggedOut]
  );
  return (
    <div className="app">
      <div className="app-chat">
        <NyceBuddyChat
          ref={ref}
          baseUrl={client.baseUrl}
          client={client}
          auth={session}
          system={login.system}
          language={login.language}
          theme={theme}
          onThemeChange={onThemeChange}
          fontSize={prefs.fontSize()}
          layout="app"
          debug={prefs.debug()}
          features={login.features}
          userName={login.username}
          userCode={login.userCode}
          systemName={login.systemName}
          organizationName={login.organizationName}
          tenantName={login.tenantName}
          limitStatus={login.limitStatus ?? null}
          onEvent={onEvent}
          onLogoutRequested={() => void logout().then(onLoggedOut)}
        />
      </div>
    </div>
  );
}
